import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const SessionTimer: React.FC = () => {
  const { sessionExpiresAt, logout } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [isExpiringSoon, setIsExpiringSoon] = useState(false);

  useEffect(() => {
    if (!sessionExpiresAt) {
      setTimeRemaining('');
      return;
    }

    const updateTimer = () => {
      const now = Date.now();
      const remaining = sessionExpiresAt - now;

      if (remaining <= 0) {
        setTimeRemaining('Session expired');
        logout();
        return;
      }

      // Calculate hours, minutes, seconds
      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

      // Format time string
      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m`);
      } else if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${seconds}s`);
      } else {
        setTimeRemaining(`${seconds}s`);
      }

      // Mark as expiring soon if less than 30 minutes remaining
      setIsExpiringSoon(remaining < 30 * 60 * 1000);
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [sessionExpiresAt, logout]);

  if (!sessionExpiresAt || !timeRemaining) {
    return null;
  }

  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
        isExpiringSoon
          ? 'bg-red-900/20 text-red-400 border border-red-500/30'
          : 'bg-surface/50 text-muted-foreground border border-border'
      }`}
      title={`Your session will expire at ${new Date(sessionExpiresAt).toLocaleTimeString()}`}
    >
      <Clock className={`w-4 h-4 ${isExpiringSoon ? 'animate-pulse' : ''}`} />
      <span className="font-medium">{timeRemaining}</span>
    </div>
  );
};

export default SessionTimer;

